<template>
  <main class="users-page">
    <div class="bg" aria-hidden="true"></div>

    <div class="container">
      <header class="header">
        <span class="kicker">
          <svg class="kicker-cross" viewBox="-34 -40 453 752" fill="none" stroke="currentColor" stroke-width="30" stroke-linejoin="round" aria-hidden="true">
            <path d="M100 40 Q190 98 285 40 Q256 112 238 188 Q300 170 355 165 Q330 245 355 345 Q300 322 238 304 Q250 450 255 600 Q285 622 290 645 Q192 598 100 645 Q130 622 130 600 Q135 450 146 304 Q90 322 30 345 Q55 245 30 165 Q90 170 146 188 Q128 112 100 40 Z"/>
            <circle cx="192" cy="245" r="70"/>
          </svg>
          Communauté
        </span>
        <h1>Rechercher des <em>membres</em></h1>
        <p>Retrouvez les personnes, associations et commerces de la communauté.</p>
      </header>

      <label class="search">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
        <input
          v-model="query"
          type="search"
          placeholder="Rechercher un nom..."
          aria-label="Rechercher un membre"
          autocomplete="off"
        />
      </label>

      <div class="chips" role="group" aria-label="Filtrer par type de compte">
        <button
          v-for="t in types"
          :key="t.value"
          type="button"
          :class="{ active: type === t.value }"
          @click="type = t.value"
        >
          <TypeIcon v-if="t.value" :type="t.value" class="chip-icon" />
          {{ t.label }}
        </button>
      </div>

      <p v-if="!hasQuery" class="hint">Saisissez au moins 2 caractères pour lancer la recherche.</p>

      <p v-else-if="loading" class="hint" aria-live="polite">
        <span class="loader"></span> Recherche en cours...
      </p>

      <div v-else-if="error" class="error" role="alert">{{ error }}</div>

      <template v-else-if="searched">
        <p class="count">
          <strong>{{ users.length }}</strong> membre{{ users.length > 1 ? "s" : "" }} trouvé{{ users.length > 1 ? "s" : "" }}
        </p>

        <div v-if="users.length" class="grid">
          <router-link
            v-for="u in users"
            :key="u.id"
            :to="`/profile/${u.id}`"
            class="user-card"
          >
            <div class="avatar">
              <img v-if="imageUrl(u.profileImage)" :src="imageUrl(u.profileImage)" :alt="`Photo de ${u.name}`" loading="lazy" />
              <span v-else>{{ initial(u) }}</span>
            </div>
            <div class="info">
              <h3>{{ u.name }}</h3>
              <span class="badge" :class="`badge-${(u.accountType || 'Personal').toLowerCase()}`">
                <TypeIcon :type="u.accountType || 'Personal'" class="badge-icon" />
                {{ typeLabel(u.accountType) }}
              </span>
              <small v-if="formatDate(u.createdAt)">Membre depuis le {{ formatDate(u.createdAt) }}</small>
            </div>
            <svg class="chevron" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg>
          </router-link>
        </div>

        <div v-else class="empty">
          <h3>Aucun membre trouvé</h3>
          <p>Essayez une autre orthographe ou changez le type de compte.</p>
        </div>
      </template>
    </div>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { useRouter } from "vue-router"
import axios from "axios"
import API_URL from "@/config/api"
import TypeIcon from "@/components/TypeIcon.vue"

const router = useRouter()

const types = [
  { value: "", label: "Tous" },
  { value: "Personal", label: "Personnel" },
  { value: "Association", label: "Association" },
  { value: "Business", label: "Commerce" },
  { value: "Other", label: "Autre" }
]

const query = ref("")
const type = ref("")
const users = ref([])
const loading = ref(false)
const error = ref("")
const searched = ref(false)

let timer = null
let controller = null

const hasQuery = computed(() => query.value.trim().length >= 2)

const imageUrl = (path) => {
  if (!path) return null
  return path.startsWith("http") ? path : `${API_URL}${path}`
}

const initial = (u) => String(u.name || "?").trim().charAt(0).toUpperCase()

const typeLabel = (value) =>
  ({ Personal: "Personnel", Association: "Association", Business: "Commerce", Other: "Autre" }[value] || "Personnel")

const formatDate = (value) => {
  if (!value) return ""
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
}

const runSearch = async () => {
  if (controller) controller.abort()

  if (!hasQuery.value) {
    users.value = []
    searched.value = false
    loading.value = false
    error.value = ""
    return
  }

  const mine = new AbortController()
  controller = mine
  loading.value = true
  error.value = ""

  try {
    const { data } = await axios.get(`${API_URL}/api/users/search`, {
      params: { q: query.value.trim(), type: type.value || undefined },
      signal: mine.signal
    })
    users.value = data
    searched.value = true
  } catch (e) {
    if (axios.isCancel(e)) return
    if (e.response?.status === 401) {
      router.push("/login")
      return
    }
    users.value = []
    error.value = e.response?.data?.message || "La recherche est momentanément indisponible."
  } finally {
    if (controller === mine) loading.value = false
  }
}

watch([query, type], () => {
  clearTimeout(timer)
  timer = setTimeout(runSearch, 350)
})

onMounted(() => {
  let stored = null
  try {
    stored = JSON.parse(localStorage.getItem("user"))
  } catch {
    stored = null
  }
  if (!stored?.token) router.push("/login")
})

onBeforeUnmount(() => {
  clearTimeout(timer)
  if (controller) controller.abort()
})
</script>

<style scoped>
.users-page {
  --red: #d90012;
  --red-deep: #8f0010;
  --blue: #0033a0;
  --orange: #f2a800;
  --ink: #1c1511;
  --ink-soft: #3a2f28;
  --muted: #6e6259;
  --paper: #faf6ef;
  --line: rgba(28, 21, 17, 0.12);
  --serif: "Fraunces", "Noto Serif Armenian", Georgia, serif;
  --sans: "Instrument Sans", "Inter", system-ui, sans-serif;

  position: relative;
  min-height: 100vh;
  padding: 120px clamp(16px, 5vw, 72px) 90px;
  overflow: hidden;
  background: var(--paper);
  color: var(--ink);
  font-family: var(--sans);
}
.users-page *,
.users-page *::before,
.users-page *::after {
  box-sizing: border-box;
}
.container {
  position: relative;
  z-index: 1;
  max-width: 980px;
  margin: 0 auto;
}

.header { margin-bottom: 34px; }
.kicker {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--red);
  font: 700 13px/1 var(--sans);
  letter-spacing: 0.16em;
  text-transform: uppercase;
}
.kicker::before {
  width: 28px;
  height: 2px;
  background: var(--red);
  content: "";
}
.kicker-cross { width: 17px; height: 28px; }
.header h1 {
  margin: 16px 0 0;
  font: 600 clamp(36px, 5vw, 60px) / 1.06 var(--serif);
  letter-spacing: -0.03em;
}
.header h1 em { color: var(--red); font-weight: 500; }
.header p {
  max-width: 560px;
  margin: 16px 0 0;
  color: var(--ink-soft);
  font-size: 17px;
  line-height: 1.7;
}

.search {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 60px;
  padding: 0 20px;
  border: 1.5px solid transparent;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 14px 34px rgba(28, 21, 17, 0.08);
  color: var(--muted);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.search:focus-within {
  border-color: var(--red);
  box-shadow: 0 0 0 4px rgba(217, 0, 18, 0.08);
}
.search input {
  flex: 1;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: none;
  background: transparent;
  color: var(--ink);
  font: 500 17px var(--sans);
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 18px 0 26px;
}
.chips button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 42px;
  padding: 0 18px;
  border: 1.5px solid var(--line);
  border-radius: 999px;
  background: #fff;
  color: var(--ink);
  font: 700 14px var(--sans);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
}
.chips button:hover { transform: translateY(-2px); }
.chips button.active {
  border-color: var(--ink);
  background: var(--ink);
  color: #fff;
}
.chip-icon { font-size: 18px; }

.hint,
.count {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 18px;
  color: var(--muted);
  font-size: 15px;
}
.count strong { color: var(--ink); }
.loader {
  width: 16px;
  height: 16px;
  border: 2.5px solid rgba(217, 0, 18, 0.25);
  border-top-color: var(--red);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
.error {
  padding: 16px 18px;
  border: 1px solid rgba(217, 0, 18, 0.3);
  border-radius: 18px;
  background: #fff1ef;
  color: var(--red-deep);
  font-weight: 600;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
  gap: 16px;
}
.user-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px;
  border: 1px solid var(--line);
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 10px 26px rgba(28, 21, 17, 0.06);
  color: inherit;
  text-decoration: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.user-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 18px 38px rgba(28, 21, 17, 0.12);
}
.user-card:focus-visible {
  outline: 3px solid var(--orange);
  outline-offset: 3px;
}
.avatar {
  display: grid;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  place-items: center;
  overflow: hidden;
  border-radius: 50%;
  background: var(--ink);
  color: var(--orange);
  font: 700 26px/1 var(--serif);
}
.avatar img { width: 100%; height: 100%; object-fit: cover; }
.info { display: flex; min-width: 0; flex-direction: column; gap: 5px; }
.info h3 {
  margin: 0;
  overflow: hidden;
  font-size: 17px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.info small { color: var(--muted); font-size: 12.5px; }
.chevron {
  margin-left: auto;
  flex-shrink: 0;
  color: var(--muted);
  transition: transform 0.25s ease, color 0.25s ease;
}
.user-card:hover .chevron {
  color: var(--red);
  transform: translateX(4px);
}
.badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: 999px;
  font: 700 11px/1.2 var(--sans);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.badge-icon { font-size: 13px; }
.badge-personal { background: #eef2ff; color: var(--blue); }
.badge-association { background: #fff1ef; color: var(--red); }
.badge-business { background: #fff6dd; color: #8a5d00; }
.badge-other { background: #f3ecdf; color: var(--ink-soft); }

.empty {
  padding: 50px 20px;
  border: 1px dashed rgba(28, 21, 17, 0.25);
  border-radius: 24px;
  text-align: center;
}
.empty h3 { margin: 0 0 8px; font: 600 24px var(--serif); }
.empty p { margin: 0; color: var(--muted); }

@keyframes spin { to { transform: rotate(360deg); } }

@media (prefers-reduced-motion: reduce) {
  * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
}
</style>